/**
 * Files API Module
 *
 * Handles file upload, download, list, and delete operations with the fileserver.
 * Supports both simple uploads (< 100MB) and chunked uploads for larger files.
 */

import axios from 'axios';

// Get auth headers for file operations
// Authentication is Bearer-only: AuthContext sets the in-memory JWT as the axios
// default Authorization header. There is no auth cookie, so the BROWSER cannot
// fetch an authenticated URL directly (new tab, <img>, <a href>): use
// openFileInNewTab() / getSignedDownloadUrl() instead (brainstorm-j1va).
const getAuthHeaders = () => {
  return {
    'X-APII-KEY': 'mysecureapikey' // Note: matches existing typo in other APIs
  };
  // The Authorization header is applied automatically by axios defaults
};

/**
 * Ask fileserver for a short-lived (5 min) signed download URL.
 * Authenticated with the axios default Bearer; the returned URL needs no auth.
 *
 * @param {string} fileId
 * @returns {Promise<string>} URL (absolute, or root-relative)
 */
export async function getSignedDownloadUrl(fileId) {
  const response = await axios.post(`/api/v1/files/${fileId}/download-link`);
  return response.data.url;
}

/**
 * Open an authenticated file in a new tab. A blank window is opened
 * synchronously inside the click handler (so popup blockers allow it) and its
 * location is set once the signed URL arrives; it is closed on failure.
 *
 * @param {string} fileId
 */
export async function openFileInNewTab(fileId) {
  const win = window.open('', '_blank');
  if (win) {
    try { win.opener = null; } catch (e) { /* ignore */ }
  }
  try {
    const url = await getSignedDownloadUrl(fileId);
    if (win) {
      win.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    if (win) win.close();
    console.error('Failed to open file:', error);
    throw new Error(error.response?.data?.detail || 'Failed to open file');
  }
}

// Matches only the authenticated route; signed links carry an extra /{token}
// segment and /public/files/... URLs never match.
const FILE_DOWNLOAD_PATH_RE = /\/api\/v1\/files\/download\/([0-9a-fA-F-]{36})\/?(?:\?.*)?$/;

/** File id if `url` is an authenticated fileserver download URL, else null. */
export function internalFileIdFromUrl(url) {
  if (typeof url !== 'string') return null;
  const m = FILE_DOWNLOAD_PATH_RE.exec(url);
  return m ? m[1] : null;
}

/**
 * Upload a file (simple upload for files < 100MB)
 *
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback (percent: number) => void
 * @returns {Promise<Object>} Upload response with file_id and download_url
 */
export async function uploadFile(file, onProgress, { workflowId, isPublic = true } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  if (workflowId) formData.append('workflow_id', workflowId);

  const params = new URLSearchParams();
  params.append('is_public', isPublic ? 'true' : 'false');

  try {
    const response = await axios.post(`/api/v1/files/upload?${params}`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    });

    return response.data;
  } catch (error) {
    console.error('File upload failed:', error);
    throw new Error(error.response?.data?.detail || 'Failed to upload file');
  }
}

/**
 * List files with pagination and filtering
 *
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of files to return (default: 50)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {string} options.file_type - Filter by file_type ('upload' or 'output')
 * @returns {Promise<Object>} { files: Array, total: number }
 */
export async function listFiles({ limit = 50, offset = 0, file_type = null } = {}) {
  try {
    const params = new URLSearchParams({ limit, offset });
    if (file_type) {
      params.append('file_type', file_type);
    }

    const response = await axios.get(`/api/v1/files/?${params.toString()}`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error) {
    console.error('Failed to list files:', error);
    throw new Error(error.response?.data?.detail || 'Failed to list files');
  }
}

/**
 * Get file metadata
 *
 * @param {string} fileId - File UUID
 * @returns {Promise<Object>} File metadata
 */
export async function getFileMetadata(fileId) {
  try {
    const response = await axios.get(`/api/v1/files/${fileId}`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error) {
    console.error('Failed to get file metadata:', error);
    throw new Error(error.response?.data?.detail || 'Failed to get file metadata');
  }
}

/**
 * Download a file
 *
 * @param {string} fileId - File UUID
 * @param {string} filename - Original filename for saving
 * @returns {Promise<void>} Downloads file to browser
 */
export async function downloadFile(fileId, filename) {
  try {
    const response = await axios.get(`/api/v1/files/download/${fileId}`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });

    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download file:', error);
    throw new Error(error.response?.data?.detail || 'Failed to download file');
  }
}

// MIME types we can usefully send as text context to the AI
const TEXT_MIMES = new Set([
  'text/plain', 'text/csv', 'text/tab-separated-values', 'text/markdown',
  'application/json', 'application/xml', 'text/xml', 'text/html',
  'application/javascript', 'text/javascript', 'text/x-python',
  'application/x-python', 'text/x-sh', 'application/x-sh',
  'application/csv', 'text/comma-separated-values',
]);

function isTextMime(mimeType = '') {
  if (TEXT_MIMES.has(mimeType)) return true;
  if (mimeType.startsWith('text/')) return true;
  return false;
}

/**
 * Fetch a file's raw text content for use as AI context.
 * Only works for text-based MIME types. Returns null for binary files.
 *
 * @param {string} fileId - File UUID
 * @param {string} mimeType - File MIME type
 * @param {number} maxBytes - Max bytes to fetch (default 80 KB)
 * @returns {Promise<string|null>}
 */
export async function fetchFileText(fileId, mimeType, maxBytes = 20 * 1024) {
  if (!isTextMime(mimeType)) return null;
  try {
    const response = await axios.get(`/api/v1/files/download/${fileId}`, {
      headers: getAuthHeaders(),
      responseType: 'text',
      transformResponse: [(data) => data], // prevent JSON parse
    });
    const text = typeof response.data === 'string' ? response.data : String(response.data);
    return text.slice(0, maxBytes);
  } catch (err) {
    console.warn('fetchFileText failed:', err);
    return null;
  }
}

/**
 * List workflow usage for a file (which workflows used this file)
 *
 * @param {string} fileId - File UUID
 * @returns {Promise<Object>} { usages: [{ workflow_id, role, created_at }] }
 */
export async function listFileUsage(fileId) {
  try {
    const response = await axios.get(`/api/v1/files/${fileId}/usage`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Failed to list file usage:', error);
    return { usages: [] };
  }
}

/**
 * List files for a specific workflow
 *
 * @param {string} workflowId - Workflow UUID
 * @returns {Promise<Object>} { files, total, limit, offset }
 */
export async function listWorkflowFiles(workflowId) {
  try {
    const response = await axios.get(`/api/v1/files/`, {
      params: { workflow_id: workflowId, limit: 100 },
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Failed to list workflow files:', error);
    throw new Error(error.response?.data?.detail || 'Failed to list workflow files');
  }
}

/**
 * Delete a file
 *
 * @param {string} fileId - File UUID
 * @returns {Promise<void>}
 */
export async function deleteFile(fileId) {
  try {
    await axios.delete(`/api/v1/files/${fileId}`, {
      headers: getAuthHeaders()
    });
  } catch (error) {
    console.error('Failed to delete file:', error);
    throw new Error(error.response?.data?.detail || 'Failed to delete file');
  }
}

/**
 * Initiate chunked upload for large files
 *
 * @param {string} filename - Original filename
 * @param {number} fileSize - Total file size in bytes
 * @param {number} chunkSize - Size of each chunk (default: 5MB)
 * @param {string} mimeType - MIME type of file
 * @returns {Promise<Object>} { upload_id, total_chunks, chunk_size }
 */
export async function initiateChunkedUpload(filename, fileSize, chunkSize = 5 * 1024 * 1024, mimeType = 'application/octet-stream') {
  try {
    const response = await axios.post('/api/v1/files/upload/chunked/initiate', {
      filename,
      file_size: fileSize,
      chunk_size: chunkSize,
      mime_type: mimeType,
      file_type: 'upload'
    }, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error) {
    console.error('Failed to initiate chunked upload:', error);
    throw new Error(error.response?.data?.detail || 'Failed to initiate chunked upload');
  }
}

/**
 * Upload a single chunk
 *
 * @param {string} uploadId - Upload UUID from initiation
 * @param {number} chunkNumber - Chunk number (0-indexed)
 * @param {Blob} chunkData - Chunk data
 * @param {Function} onProgress - Progress callback for this chunk
 * @returns {Promise<Object>} { chunk_number, uploaded_at }
 */
export async function uploadChunk(uploadId, chunkNumber, chunkData, onProgress) {
  try {
    const response = await axios.put(
      `/api/v1/files/upload/chunked/${uploadId}/chunk/${chunkNumber}`,
      chunkData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/octet-stream'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to upload chunk ${chunkNumber}:`, error);
    throw new Error(error.response?.data?.detail || `Failed to upload chunk ${chunkNumber}`);
  }
}

/**
 * Complete chunked upload after all chunks are uploaded
 *
 * @param {string} uploadId - Upload UUID
 * @returns {Promise<Object>} { file_id, filename, download_url }
 */
export async function completeChunkedUpload(uploadId) {
  try {
    const response = await axios.post(
      `/api/v1/files/upload/chunked/${uploadId}/complete`,
      {},
      {
        headers: getAuthHeaders()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Failed to complete chunked upload:', error);
    throw new Error(error.response?.data?.detail || 'Failed to complete chunked upload');
  }
}

/**
 * Upload large file with chunked upload and progress tracking
 *
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback (percent: number, uploadedChunks: number, totalChunks: number) => void
 * @param {number} chunkSize - Size of each chunk (default: 5MB)
 * @returns {Promise<Object>} { file_id, filename, download_url }
 */
export async function uploadLargeFile(file, onProgress, chunkSize = 5 * 1024 * 1024) {
  try {
    // Step 1: Initiate chunked upload
    const initResponse = await initiateChunkedUpload(
      file.name,
      file.size,
      chunkSize,
      file.type || 'application/octet-stream'
    );

    const { upload_id, total_chunks } = initResponse;
    let uploadedChunks = 0;

    // Step 2: Upload each chunk
    for (let chunkNumber = 0; chunkNumber < total_chunks; chunkNumber++) {
      const start = chunkNumber * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunkBlob = file.slice(start, end);

      await uploadChunk(upload_id, chunkNumber, chunkBlob, (chunkPercent) => {
        // Calculate overall progress
        const overallPercent = Math.round(
          ((uploadedChunks + (chunkPercent / 100)) / total_chunks) * 100
        );
        if (onProgress) {
          onProgress(overallPercent, uploadedChunks, total_chunks);
        }
      });

      uploadedChunks++;

      // Update progress after chunk completes
      const overallPercent = Math.round((uploadedChunks / total_chunks) * 100);
      if (onProgress) {
        onProgress(overallPercent, uploadedChunks, total_chunks);
      }
    }

    // Step 3: Complete upload
    const completeResponse = await completeChunkedUpload(upload_id);
    return completeResponse;
  } catch (error) {
    console.error('Large file upload failed:', error);
    throw error;
  }
}

/**
 * Save a canvas output node's content as a file in the fileservice.
 *
 * For textNode: serialises content as .md (or .txt if no markdown detected).
 * For imageNode: fetches the image blob from the public_url and re-uploads.
 *
 * @param {Object} nodeData  - Node's data object
 * @param {string} nodeType  - 'textNode' | 'imageNode'
 * @returns {Promise<Object>} Upload response with file_id, public_url, etc.
 */
export async function saveNodeAsFile(nodeData, nodeType) {
  const title = (nodeData.title || 'canvas-output').replace(/[^a-z0-9_\-. ]/gi, '_').trim();

  if (nodeType === 'imageNode') {
    const imageUrl = nodeData.imageUrl || nodeData.public_url;
    if (!imageUrl) throw new Error('No image URL available');

    // Fetch the image as a blob, then re-upload
    const res = await fetch(imageUrl);
    if (!res.ok) throw new Error('Failed to fetch image for saving');
    const blob = await res.blob();
    const ext = blob.type.includes('png') ? '.png' : blob.type.includes('webp') ? '.webp' : '.jpg';
    const file = new File([blob], `${title}${ext}`, { type: blob.type });
    return await uploadFile(file, null, { isPublic: true });
  }

  // textNode — save as markdown
  const content = nodeData.content || '';
  const ext = /^#{1,6}\s|\*\*[^*]+\*\*|^```/m.test(content) ? '.md' : '.txt';
  const blob = new Blob([content], { type: 'text/plain' });
  const file = new File([blob], `${title}${ext}`, { type: 'text/plain' });
  return await uploadFile(file, null, { isPublic: true });
}

/**
 * Smart upload: automatically chooses simple or chunked upload based on file size
 *
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback
 * @param {number} threshold - Size threshold for chunked upload (default: 100MB)
 * @returns {Promise<Object>} Upload response
 */
export async function smartUpload(file, onProgress, threshold = 100 * 1024 * 1024, options = {}) {
  if (file.size > threshold) {
    return await uploadLargeFile(file, onProgress);
  } else {
    return await uploadFile(file, onProgress, options);
  }
}
